// Turns the flat stable-ID Position list into the district org chart.
// Pure — no Firebase. People are layered on in the view from contacts.

import type { AffiliatedHolder, Contact, Position } from '../types';
import { affiliatedHoldersOfSeat } from './affiliatedHolders.ts';

export interface OrgNode {
  position: Position;
  children: OrgNode[];
  depth: number;
}

export interface PositionResponsibilityCoverage {
  valid: boolean;
  committeeIdsByPositionId: Readonly<Record<string, readonly string[]>>;
}

/** Build the reporting forest from stable Position identities. Invalid IDs,
 * missing parents, duplicates, and cycles fail closed: no relationship is
 * inferred and no partial hierarchy is rendered. */
export function buildOrgForest(positions: Position[]): OrgNode[] {
  const byId = new Map<string, Position>();
  for (const position of positions) {
    if (!position.positionId || byId.has(position.positionId)) return [];
    byId.set(position.positionId, position);
  }
  const childrenOf = new Map<string, Position[]>();
  const roots: Position[] = [];

  for (const p of positions) {
    if (p.reportsToPositionId === p.positionId) return [];
    if (p.reportsToPositionId && !byId.has(p.reportsToPositionId)) return [];
    const parent = p.reportsToPositionId;
    if (parent) {
      const arr = childrenOf.get(parent) ?? [];
      arr.push(p);
      childrenOf.set(parent, arr);
    } else {
      roots.push(p);
    }
  }

  const seen = new Set<Position>();
  const build = (p: Position, depth: number): OrgNode => {
    seen.add(p);
    const kids = (childrenOf.get(p.positionId) ?? [])
      .filter((c) => !seen.has(c))
      .map((c) => build(c, depth + 1));
    return { position: p, children: kids, depth };
  };

  const forest = roots.map((r) => build(r, 0));
  return seen.size === positions.length ? forest : [];
}

/** Read-only direct + supervised responsibility coverage for Settings.
 * The stable Position graph is the only source. Any malformed relationship
 * disables inherited coverage instead of silently treating nodes as roots. */
export function buildPositionResponsibilityCoverage(
  positions: Position[],
  knownCommitteeIds: readonly string[],
): PositionResponsibilityCoverage {
  const knownCommittees = new Set(knownCommitteeIds);
  const directValid = positions.every((position) =>
    !position.committee || knownCommittees.has(position.committee));
  const forest = buildOrgForest(positions);
  if (!directValid || (positions.length > 0 && forest.length === 0)) {
    return { valid: false, committeeIdsByPositionId: Object.freeze({}) };
  }

  const coverage: Record<string, readonly string[]> = {};
  const visit = (node: OrgNode): Set<string> => {
    const ids = new Set<string>(node.position.committee ? [node.position.committee] : []);
    for (const child of node.children) {
      for (const id of visit(child)) ids.add(id);
    }
    coverage[node.position.positionId] = Object.freeze([...ids].sort());
    return ids;
  };
  for (const root of forest) visit(root);
  return { valid: true, committeeIdsByPositionId: Object.freeze(coverage) };
}

/** Contacts holding a position in a district, sorted by name (vacancies last). */
export function holdersOf(
  positionName: string,
  contacts: Contact[],
  districtId: string,
): Contact[] {
  return contacts
    .filter((c) => c.districtId === districtId && c.positions.includes(positionName))
    .sort((a, b) => {
      const av = !a.name || /vacant/i.test(a.name);
      const bv = !b.name || /vacant/i.test(b.name);
      return av === bv ? a.name.localeCompare(b.name) : av ? 1 : -1;
    });
}

export interface OrgForestIssue {
  type: 'duplicate' | 'self-reference' | 'missing-parent' | 'cycle';
  positionId: string;
  positionName: string;
  detail: string;
}

export interface OrgChartForestResult {
  forest: OrgNode[];
  issues: OrgForestIssue[];
}

/** Org Chart V2 — a best-effort, never-empty counterpart to
 *  buildOrgForest() for the visual Chart. buildOrgForest() intentionally
 *  fails CLOSED (returns []) on any malformed Reports To data, which is
 *  the right behavior for buildPositionResponsibilityCoverage() (an
 *  authorization-adjacent computation that must not guess). The Chart is
 *  just a rendering of already-configured, non-authoritative structure —
 *  losing every Position because ONE Reports To link is broken would hide
 *  real people from a view administrators rely on to spot exactly that
 *  problem. So this degrades gracefully instead: a Position with a
 *  duplicate ID, a self-reference, an unknown parent, or a reporting
 *  cycle is demoted to a top-level root (never dropped), and the problem
 *  is reported in `issues` for the UI to surface — never silently. */
export function buildOrgChartForest(positions: Position[]): OrgChartForestResult {
  const issues: OrgForestIssue[] = [];
  const byId = new Map<string, Position>();
  for (const p of positions) {
    if (!p.positionId) continue;
    if (byId.has(p.positionId)) {
      issues.push({
        type: 'duplicate',
        positionId: p.positionId,
        positionName: p.name,
        detail: 'Duplicate Position ID — only the first is shown.',
      });
      continue;
    }
    byId.set(p.positionId, p);
  }

  const effectiveParent = new Map<string, string | null>();
  for (const p of byId.values()) {
    const parent = p.reportsToPositionId;
    if (!parent) {
      effectiveParent.set(p.positionId, null);
    } else if (parent === p.positionId) {
      issues.push({
        type: 'self-reference',
        positionId: p.positionId,
        positionName: p.name,
        detail: 'Reports To itself — shown as top-level instead.',
      });
      effectiveParent.set(p.positionId, null);
    } else if (!byId.has(parent)) {
      issues.push({
        type: 'missing-parent',
        positionId: p.positionId,
        positionName: p.name,
        detail: 'Reports To an unknown Position — shown as top-level instead.',
      });
      effectiveParent.set(p.positionId, null);
    } else {
      effectiveParent.set(p.positionId, parent);
    }
  }

  // Cycle protection: walk each Position's ancestor chain. If a walk
  // revisits a Position already seen in that same walk, the closing edge
  // (the last one added) is cut, breaking the cycle at exactly one point.
  // Mutating effectiveParent immediately means later walks into the same
  // cycle just stop at the cut instead of re-detecting and re-reporting it.
  for (const startId of byId.keys()) {
    const chain: string[] = [];
    const visited = new Set<string>();
    let cur: string | null = startId;
    while (cur) {
      if (visited.has(cur)) {
        const closingId = chain[chain.length - 1];
        if (closingId && effectiveParent.get(closingId) !== null) {
          const p = byId.get(closingId)!;
          issues.push({
            type: 'cycle',
            positionId: closingId,
            positionName: p.name,
            detail: 'Part of a Reports To cycle — shown as top-level instead.',
          });
          effectiveParent.set(closingId, null);
        }
        break;
      }
      visited.add(cur);
      chain.push(cur);
      cur = effectiveParent.get(cur) ?? null;
    }
  }

  const childrenOf = new Map<string, Position[]>();
  const roots: Position[] = [];
  for (const p of byId.values()) {
    const parent = effectiveParent.get(p.positionId) ?? null;
    if (parent) {
      const arr = childrenOf.get(parent) ?? [];
      arr.push(p);
      childrenOf.set(parent, arr);
    } else {
      roots.push(p);
    }
  }
  const build = (p: Position, depth: number): OrgNode => ({
    position: p,
    children: (childrenOf.get(p.positionId) ?? []).map((c) => build(c, depth + 1)),
    depth,
  });

  return { forest: roots.map((r) => build(r, 0)), issues };
}

/** Every Position, grouped by its committee/organizational-area id ('' for
 *  positions with none). The generic counterpart to
 *  groupCouncilPositionsByArea() — caller pre-filters by
 *  organizationLevel; this makes no level assumption, so it works for a
 *  district's Roster view too. */
export function groupPositionsByCommittee(positions: Position[]): Map<string, Position[]> {
  const byArea = new Map<string, Position[]>();
  for (const p of positions) {
    const list = byArea.get(p.committee) ?? [];
    list.push(p);
    byArea.set(p.committee, list);
  }
  return byArea;
}

/** Council Organization Phase B — the council-scoped counterpart to
 *  holdersOf(). A council-level Position (see Position.organizationLevel)
 *  is held council-wide, not once per district, so this deliberately does
 *  NOT filter by districtId — a holder can be rostered in any district.
 *  Never used for authorization; purely who's shown as occupying the seat. */
export function holdersOfCouncilPosition(
  positionName: string,
  contacts: Contact[],
): Contact[] {
  return contacts
    .filter((c) => c.positions.includes(positionName))
    .sort((a, b) => {
      const av = !a.name || /vacant/i.test(a.name);
      const bv = !b.name || /vacant/i.test(b.name);
      return av === bv ? a.name.localeCompare(b.name) : av ? 1 : -1;
    });
}

/** Every council-level Position (see Position.organizationLevel), grouped
 *  by its committee/organizational-area id ('' for positions with none —
 *  e.g. top-level council leadership not tied to one committee). Council
 *  Organization Phase B's basic building block for a "modest" Org Chart
 *  council view: no separate tree-building needed beyond this grouping,
 *  since council leadership is typically shown as area-by-area rosters
 *  rather than a deep reporting tree. reportsToPositionId is still honored
 *  by buildOrgForest() for any caller that wants the fuller hierarchy. */
export function groupCouncilPositionsByArea(
  positions: Position[],
): Map<string, Position[]> {
  const byArea = new Map<string, Position[]>();
  for (const p of positions) {
    if ((p.organizationLevel ?? 'district') !== 'council') continue;
    const list = byArea.get(p.committee) ?? [];
    list.push(p);
    byArea.set(p.committee, list);
  }
  return byArea;
}

/** Flatten a node's whole subtree (incl. itself) — used for vacancy counts. */
export function flattenNodes(nodes: OrgNode[]): OrgNode[] {
  const out: OrgNode[] = [];
  const walk = (n: OrgNode) => {
    out.push(n);
    n.children.forEach(walk);
  };
  nodes.forEach(walk);
  return out;
}

/** filled/total seat count for one district across the whole position
 *  catalog (the org forest) — a position with zero non-vacant holders in
 *  this district counts as an empty seat, REGARDLESS of whether a Contact
 *  doc for it exists at all. This is deliberately NOT "how many Contact
 *  records exist and aren't named Vacant" — a position with no Contact
 *  record whatsoever is just as vacant as one with a literal "Vacant"
 *  placeholder, and counting only existing docs would make a
 *  barely-staffed district look artificially "100% filled." Matches
 *  exactly what the Org Chart page's own "Seats filled" figure and vacant
 *  seat styling already show. */
export function countSeats(
  forest: OrgNode[],
  contacts: Contact[],
  district: string,
  /** People who hold a seat in this district from outside it (District Affiliations). They fill a seat that is defined already;
   *  they never add one, so the total is unchanged. Absent = exactly the roster-only count. */
  affiliated: readonly AffiliatedHolder[] = [],
): { filled: number; seats: number } {
  let filled = 0;
  let seats = 0;
  for (const node of flattenNodes(forest)) {
    const held = holdersOf(node.position.name, contacts, district).filter(
      (c) => c.name && !/vacant/i.test(c.name),
    ).length;
    const heldFromOutside = affiliatedHoldersOfSeat(affiliated, node.position.positionId, district).length;
    seats += 1;
    filled += held > 0 || heldFromOutside > 0 ? 1 : 0;
  }
  return { filled, seats };
}

/** The council-scoped counterpart to countSeats() — uses
 *  holdersOfCouncilPosition() (no districtId filter) instead of
 *  holdersOf(), matching how council seats are actually occupied. */
export function countSeatsCouncil(
  forest: OrgNode[],
  contacts: Contact[],
): { filled: number; seats: number } {
  let filled = 0;
  let seats = 0;
  for (const node of flattenNodes(forest)) {
    const held = holdersOfCouncilPosition(node.position.name, contacts).filter(
      (c) => c.name && !/vacant/i.test(c.name),
    ).length;
    seats += 1;
    filled += held > 0 ? 1 : 0;
  }
  return { filled, seats };
}
